import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Timer, X, Palette, Layers } from 'lucide-react';
import api from '@/lib/api';
import { usePlayer } from '@/contexts/PlayerContext';
import DownloadButton from '@/components/DownloadButton';

const TIMER_OPTIONS = [5, 10, 20, 30, 45, 60];

export default function Music() {
    const { playTrack, setTimerMinutes, timerMinutes, track } = usePlayer();
    const [tracks, setTracks] = useState([]);
    const [pending, setPending] = useState(null);
    const [showTimerFor, setShowTimerFor] = useState(null);

    useEffect(() => {
        api.get('/music').then((r) => setTracks(r.data || [])).catch(() => {});
    }, []);

    const begin = (t, minutes) => {
        playTrack({
            id: t.id, title: t.title, subtitle: t.category,
            image: t.cover_image, audio_url: t.audio_url, loop: t.loop,
            kind: 'music', duration_minutes: minutes || 0,
        });
        if (minutes) setTimerMinutes(minutes);
        setShowTimerFor(null);
    };

    return (
        <div className="space-y-8" data-testid="music-page">
            <header>
                <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Soundscapes</p>
                <h1 className="font-serif text-4xl md:text-5xl text-forest mt-2">Set a quiet backdrop</h1>
                <p className="text-forest-soft mt-2 max-w-xl">
                    Loops gently in the background. Add a timer if you want it to fade away on its own.
                </p>
            </header>

            <Link
                to="/app/sound"
                data-testid="color-sound-cta"
                className="qm-card p-5 md:p-6 flex items-center gap-4 hover:shadow-md transition group"
                style={{ backgroundImage: 'linear-gradient(115deg, rgba(168,197,176,0.25), rgba(242,196,196,0.25) 55%, rgba(200,160,216,0.25))' }}
            >
                <div className="w-12 h-12 rounded-2xl bg-white/70 grid place-items-center shrink-0">
                    <Palette size={20} className="text-forest" />
                </div>
                <div className="flex-1">
                    <div className="font-serif text-xl text-forest">Colors of sound</div>
                    <div className="text-sm text-forest-soft">
                        Continuous white, pink, brown, green, blue, violet and grey noise. Pick a purpose &mdash; focus, sleep, study, relax &mdash; and we&rsquo;ll pre-pick the right colour.
                    </div>
                </div>
                <div className="text-forest opacity-60 group-hover:opacity-100 shrink-0">→</div>
            </Link>

            <Link
                to="/app/soundscape"
                data-testid="soundscape-cta"
                className="qm-card p-5 md:p-6 flex items-center gap-4 hover:shadow-md transition group"
                style={{ backgroundImage: 'linear-gradient(115deg, rgba(200,160,216,0.25), rgba(168,197,176,0.25) 55%, rgba(242,196,196,0.25))' }}
            >
                <div className="w-12 h-12 rounded-2xl bg-white/70 grid place-items-center shrink-0">
                    <Layers size={20} className="text-forest" />
                </div>
                <div className="flex-1">
                    <div className="font-serif text-xl text-forest">Soundscape Mixer</div>
                    <div className="text-sm text-forest-soft">
                        Blend a colour of noise with rain, waves or forest. Save your favourite mixes and play curated presets.
                    </div>
                </div>
                <div className="text-forest opacity-60 group-hover:opacity-100 shrink-0">→</div>
            </Link>

            {timerMinutes && track?.kind === 'music' && (
                <div className="qm-card p-4 flex items-center justify-between" data-testid="timer-banner">
                    <div className="flex items-center gap-3 text-forest">
                        <Timer size={18} />
                        <span>Timer set — fading after about {timerMinutes} minutes.</span>
                    </div>
                    <button onClick={() => setTimerMinutes(null)} className="qm-btn-ghost text-sm" data-testid="timer-clear-button">
                        <X size={16} className="inline mr-1" /> Clear
                    </button>
                </div>
            )}

            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="music-grid">
                {tracks.map((t) => (
                    <li key={t.id} className="qm-card overflow-hidden group hover:-translate-y-1 hover:shadow-lg transition-all">
                        <div
                            className="aspect-square relative"
                            style={{
                                backgroundImage: `linear-gradient(180deg, transparent 40%, rgba(45,74,62,0.45)), url(${t.cover_image})`,
                                backgroundSize: 'cover', backgroundPosition: 'center',
                            }}
                        >
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                                <div className="text-cream">
                                    <div className="font-serif text-lg leading-tight">{t.title}</div>
                                    <div className="text-xs uppercase tracking-wider opacity-80">{t.category}</div>
                                </div>
                                <button
                                    onClick={() => begin(t)}
                                    data-testid={`play-music-${t.id}`}
                                    aria-label={`Play ${t.title}`}
                                    className="rounded-full w-11 h-11 grid place-items-center bg-cream text-forest hover:scale-110 active:scale-95 transition"
                                >
                                    <Play size={18} className="ml-0.5" />
                                </button>
                            </div>
                        </div>
                        <div className="p-4 flex items-center justify-between gap-3">
                            <p className="text-sm text-forest-soft line-clamp-2">{t.description}</p>
                            <DownloadButton
                                item={{ id: t.id, title: t.title, audio_url: t.audio_url, category: t.category, kind: 'music', cover_image: t.cover_image }}
                                className="!w-8 !h-8"
                                testIdPrefix="dl-music"
                            />
                            <button
                                onClick={() => setShowTimerFor(showTimerFor === t.id ? null : t.id)}
                                data-testid={`timer-toggle-${t.id}`}
                                className="qm-pill text-xs border-forest/20 hover:bg-sage/30 inline-flex items-center gap-1"
                            >
                                <Timer size={14} /> Timer
                            </button>
                        </div>
                        {showTimerFor === t.id && (
                            <div className="px-4 pb-4 -mt-1 flex flex-wrap gap-2 animate-fade-in" data-testid={`timer-options-${t.id}`}>
                                {TIMER_OPTIONS.map((m) => (
                                    <button
                                        key={m}
                                        onClick={() => begin(t, m)}
                                        data-testid={`timer-${t.id}-${m}`}
                                        className="qm-pill text-xs border-forest/20 hover:bg-forest hover:text-cream"
                                    >
                                        {m} min
                                    </button>
                                ))}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
